import React, { useState, useRef } from 'react';
import './ImportGroupsModal.css';

interface ImportGroupsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (csvData: string, mode: 'merge' | 'replace') => Promise<{
    success: boolean;
    message: string;
    data: {
      created: number;
      updated: number;
      skipped: number;
      errors: string[];
    };
  }>;
  onDownloadTemplate: () => void;
}

interface ParsedRow {
  groupName: string;
  description: string;
  rowNumber: number;
  isValid: boolean;
  error?: string;
}

const ImportGroupsModal: React.FC<ImportGroupsModalProps> = ({
  isOpen,
  onClose,
  onImport,
  onDownloadTemplate
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvContent = event.target?.result as string;
        setCsvData(csvContent);
        parseCSVPreview(csvContent);
      };
      reader.readAsText(file);
    }
  };

  const parseCSVPreview = (csvContent: string) => {
    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
      setParsedRows([]);
      setShowPreview(false);
      return;
    }

    const rows: ParsedRow[] = [];
    
    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      try {
        // Parse CSV line (handle quoted values)
        const matches = line.match(/(?:^|,)("(?:[^"]|"")*"|[^,]*)/g);
        if (!matches) {
          rows.push({
            groupName: '',
            description: '',
            rowNumber: i + 1,
            isValid: false,
            error: 'Invalid CSV format'
          });
          continue;
        }

        const values = matches.map(v => {
          let val = v.replace(/^,/, '').trim();
          if (val.startsWith('"') && val.endsWith('"')) {
            val = val.slice(1, -1).replace(/""/g, '"');
          }
          return val;
        });

        const groupName = values[0]?.trim() || '';
        const description = values[1]?.trim() || '';

        if (!groupName) {
          rows.push({
            groupName: '',
            description,
            rowNumber: i + 1,
            isValid: false,
            error: 'Group name is required'
          });
        } else {
          rows.push({
            groupName,
            description,
            rowNumber: i + 1,
            isValid: true
          });
        }
      } catch (error) {
        rows.push({
          groupName: '',
          description: '',
          rowNumber: i + 1,
          isValid: false,
          error: 'Parse error'
        });
      }
    }

    setParsedRows(rows);
    setShowPreview(true);
  };

  const handleImport = async () => {
    if (!csvData) return;

    setIsImporting(true);
    try {
      const result = await onImport(csvData, importMode);
      setImportResult(result);
    } catch (error: any) {
      setImportResult({
        success: false,
        message: error.message || 'Import failed',
        data: { created: 0, updated: 0, skipped: 0, errors: [error.message] }
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setCsvData('');
    setParsedRows([]);
    setShowPreview(false);
    setImportResult(null);
    setImportMode('merge');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const validRowCount = parsedRows.filter(row => row.isValid).length;
  const invalidRowCount = parsedRows.filter(row => !row.isValid).length;

  if (!isOpen) return null;

  return (
    <div className="import-modal-overlay" onClick={handleClose}>
      <div className="import-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="import-modal-header">
          <h2>Import Location Groups</h2>
          <button className="import-close-button" onClick={handleClose}>×</button>
        </div>

        <div className="import-modal-body">
          {!importResult ? (
            <>
              <div className="import-instructions">
                <p>Upload a CSV file to import location groups. The CSV should have two columns:</p>
                <ul>
                  <li><strong>Group Name</strong> (required) - The name of the group</li>
                  <li><strong>Description</strong> (optional) - Description of the group</li>
                </ul>
                <button 
                  className="import-download-template-btn" 
                  onClick={onDownloadTemplate}
                >
                  📥 Download Template
                </button>
              </div>

              <div className="import-file-input-section">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="import-file-input"
                  id="csv-file-input"
                />
                <label htmlFor="csv-file-input" className="import-file-label">
                  {selectedFile ? selectedFile.name : 'Choose CSV File'}
                </label>
              </div>

              {showPreview && (
                <>
                  <div className="import-mode-selection">
                    <label className="import-mode-label">
                      <strong>Import Mode:</strong>
                    </label>
                    <div className="import-mode-options">
                      <label className="import-mode-option">
                        <input
                          type="radio"
                          name="importMode"
                          value="merge"
                          checked={importMode === 'merge'}
                          onChange={(e) => setImportMode(e.target.value as 'merge')}
                        />
                        <span>
                          <strong>Merge</strong> - Add new groups and update existing ones
                        </span>
                      </label>
                      <label className="import-mode-option">
                        <input
                          type="radio"
                          name="importMode"
                          value="replace"
                          checked={importMode === 'replace'}
                          onChange={(e) => setImportMode(e.target.value as 'replace')}
                        />
                        <span>
                          <strong>Replace</strong> - Delete all existing groups and create from CSV
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="import-preview-section">
                    <h3>Preview ({validRowCount} valid, {invalidRowCount} invalid)</h3>
                    <div className="import-preview-table-container">
                      <table className="import-preview-table">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Group Name</th>
                            <th>Description</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedRows.slice(0, 20).map((row, idx) => (
                            <tr key={idx} className={row.isValid ? 'valid-row' : 'invalid-row'}>
                              <td>{row.rowNumber}</td>
                              <td>{row.groupName || '-'}</td>
                              <td className="description-cell">
                                {row.description || '-'}
                              </td>
                              <td>
                                {row.isValid ? (
                                  <span className="status-valid">✓ Valid</span>
                                ) : (
                                  <span className="status-invalid">✗ {row.error}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {parsedRows.length > 20 && (
                        <div className="import-preview-more">
                          ... and {parsedRows.length - 20} more rows
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="import-result-section">
              <h3 className={importResult.success ? 'result-success' : 'result-error'}>
                {importResult.success ? '✓ Import Completed' : '✗ Import Failed'}
              </h3>
              <div className="import-result-stats">
                <div className="result-stat">
                  <span className="stat-label">Created:</span>
                  <span className="stat-value">{importResult.data.created}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">Updated:</span>
                  <span className="stat-value">{importResult.data.updated}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">Skipped:</span>
                  <span className="stat-value">{importResult.data.skipped}</span>
                </div>
              </div>
              {importResult.data.errors.length > 0 && (
                <div className="import-errors">
                  <h4>Errors:</h4>
                  <ul>
                    {importResult.data.errors.map((error: string, idx: number) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="import-modal-footer">
          {!importResult ? (
            <>
              <button className="import-cancel-button" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="import-submit-button"
                onClick={handleImport}
                disabled={!showPreview || invalidRowCount > 0 || isImporting}
              >
                {isImporting ? 'Importing...' : `Import ${validRowCount} Groups`}
              </button>
            </>
          ) : (
            <button className="import-close-result-button" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportGroupsModal;

